import {Icon, Show, ThemeIconKey} from 'platform/foundation';
import styled, {css} from 'styled-components';
import {match} from 'ts-pattern';

import {KeyboardEvent, MouseEvent, PointerEvent, ReactNode, useRef, useState} from 'react';

import {clamp, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {sourcingStorageKey} from '../../../constants/sourcingStorageKey';
import {getPreviewLayoutStorageKeys} from '../../../utils/getPreviewLayoutStorageKeys';
import {isUnifiedSourcingEnabled} from '../../../utils/isUnifiedSourcingEnabled';

/**
 * Preview width is kept in whole percent of the layout - dragging then produces at most
 * ~50 distinct styled-components classes instead of a new one for every pixel.
 */
const DEFAULT_PREVIEW_WIDTH = 33;
const DEFAULT_MIN_PREVIEW_WIDTH = 20;
const DEFAULT_MAX_PREVIEW_WIDTH = 70;
const DEFAULT_STORAGE_KEY = sourcingStorageKey.PREVIEW_LAYOUT_PREFIX;
/** Arrow keys move the divider by this many percent, so the split is reachable without a pointer. */
const KEYBOARD_STEP = 2;

const TOGGLE_ICON: Record<'collapsed' | 'expanded', ThemeIconKey> = {
  collapsed: 'navigation/chevron_left',
  expanded: 'navigation/chevron_right',
};

type StoredLayout = {
  previewWidth: number;
  isCollapsed: boolean;
};

/**
 * Each place the layout is used keeps its own split - the vehicle list is usually read with a
 * narrow preview, while the detail page has room for a wider one.
 */
export type ResizablePreviewOptions = {
  /**
   * Distinguishes the stored split of one usage from another.
   */
  storageKey?: string;
  /**
   * Preview width limits in percent of the layout.
   */
  minWidth?: number;
  maxWidth?: number;
  /**
   * The detail page shows the preview from the notebook breakpoint up, the lists only on desktop.
   */
  isPreviewShownOnNotebook?: boolean;
  /**
   * Set where the page itself scrolls - both parts then grow with their content instead of
   * filling a container of a fixed height.
   */
  isHeightAuto?: boolean;
};

type LayoutConfig = {
  storageKey: string;
  minWidth: number;
  maxWidth: number;
};

const DEFAULT_LAYOUT: StoredLayout = {
  previewWidth: DEFAULT_PREVIEW_WIDTH,
  isCollapsed: false,
};

/**
 * Private browsing and blocked site data make localStorage throw, so both directions are guarded
 * and the layout simply falls back to the default split.
 */
function readStoredLayout(config: LayoutConfig): StoredLayout {
  const storageKeys = getPreviewLayoutStorageKeys(config.storageKey);
  try {
    const storedWidth = Number(localStorage.getItem(storageKeys.width));
    const hasUsableWidth = Number.isFinite(storedWidth) && storedWidth > 0;

    return {
      previewWidth: clamp(
        config.minWidth,
        config.maxWidth,
        hasUsableWidth ? storedWidth : DEFAULT_PREVIEW_WIDTH
      ),
      isCollapsed: localStorage.getItem(storageKeys.collapsed) === 'true',
    };
  } catch {
    return {
      ...DEFAULT_LAYOUT,
      previewWidth: clamp(config.minWidth, config.maxWidth, DEFAULT_PREVIEW_WIDTH),
    };
  }
}

function writeStoredLayout(config: LayoutConfig, layout: StoredLayout) {
  const storageKeys = getPreviewLayoutStorageKeys(config.storageKey);
  try {
    localStorage.setItem(storageKeys.width, String(layout.previewWidth));
    localStorage.setItem(storageKeys.collapsed, String(layout.isCollapsed));
  } catch {
    // Storage is unavailable - the layout still works, it just won't survive a reload.
  }
}

interface ResizablePreviewLayoutProps extends TestIdProps {
  /**
   * Left (main) part of the layout - filters header and the vehicle list.
   */
  children: ReactNode;
  /**
   * Right part of the layout - advertisement preview. Rendered on desktop only, unless
   * isPreviewShownOnNotebook is set.
   */
  preview?: ReactNode;
  /**
   * Everything that differs between the places the layout is used. Left out, it behaves as on
   * the vehicle lists.
   */
  options?: ResizablePreviewOptions;
}

/**
 * Two column layout where the user can drag the divider to resize both parts
 * and collapse the preview completely - only the divider with the toggle button stays.
 */
export function ResizablePreviewLayout(props: ResizablePreviewLayoutProps) {
  const enhancedSurfaces = isUnifiedSourcingEnabled();
  const options = props.options ?? {};
  const config: LayoutConfig = {
    storageKey: options.storageKey ?? DEFAULT_STORAGE_KEY,
    minWidth: options.minWidth ?? DEFAULT_MIN_PREVIEW_WIDTH,
    maxWidth: options.maxWidth ?? DEFAULT_MAX_PREVIEW_WIDTH,
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [storedLayout] = useState<StoredLayout>(() => readStoredLayout(config));
  const [previewWidth, setPreviewWidth] = useState<number>(storedLayout.previewWidth);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(storedLayout.isCollapsed);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const handleDragStart = (event: PointerEvent<HTMLDivElement>) => {
    if (isCollapsed) {
      return;
    }

    event.preventDefault();
    // Pointer capture keeps move/up events coming even when the cursor leaves the window, so
    // dragging can never stay stuck and no global listeners (and no effect) are needed.
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsDragging(true);
  };

  const handleDrag = (event: PointerEvent<HTMLDivElement>) => {
    const container = containerRef.current;

    if (!isDragging || isNil(container)) {
      return;
    }

    const {right, width} = container.getBoundingClientRect();

    if (width === 0) {
      return;
    }

    setPreviewWidth(
      clamp(config.minWidth, config.maxWidth, Math.round(((right - event.clientX) / width) * 100))
    );
  };

  const stopDragging = () => {
    if (!isDragging) {
      return;
    }

    setIsDragging(false);
    writeStoredLayout(config, {previewWidth, isCollapsed});
  };

  const handleDragEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    stopDragging();
  };

  const resizeTo = (width: number) => {
    const clampedWidth = clamp(config.minWidth, config.maxWidth, width);

    setPreviewWidth(clampedWidth);
    writeStoredLayout(config, {previewWidth: clampedWidth, isCollapsed});
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isCollapsed) {
      return;
    }

    match(event.key)
      .with('ArrowLeft', () => {
        event.preventDefault();
        resizeTo(previewWidth + KEYBOARD_STEP);
      })
      .with('ArrowRight', () => {
        event.preventDefault();
        resizeTo(previewWidth - KEYBOARD_STEP);
      })
      .with('Home', () => {
        event.preventDefault();
        resizeTo(config.maxWidth);
      })
      .with('End', () => {
        event.preventDefault();
        resizeTo(config.minWidth);
      })
      .otherwise(() => undefined);
  };

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsCollapsed(!isCollapsed);
    writeStoredLayout(config, {previewWidth, isCollapsed: !isCollapsed});
  };

  const toggle = match(isCollapsed)
    .with(true, () => ({
      icon: TOGGLE_ICON.collapsed,
      label: i18n.t('general.actions.unhide'),
    }))
    .otherwise(() => ({
      icon: TOGGLE_ICON.expanded,
      label: i18n.t('general.actions.hide'),
    }));

  return (
    <Container ref={containerRef} $isDragging={isDragging} $isHeightAuto={options.isHeightAuto}>
      <MainPanel $isHeightAuto={options.isHeightAuto}>{props.children}</MainPanel>
      <Show onDesktop onNotebook={options.isPreviewShownOnNotebook} when={isNotNil(props.preview)}>
        <Divider
          role="separator"
          aria-orientation="vertical"
          aria-valuemin={config.minWidth}
          aria-valuemax={config.maxWidth}
          aria-valuenow={previewWidth}
          tabIndex={isCollapsed ? -1 : 0}
          $isCollapsed={isCollapsed}
          $isDragging={isDragging}
          $enhancedSurfaces={enhancedSurfaces}
          onKeyDown={handleKeyDown}
          onPointerDown={handleDragStart}
          onPointerMove={handleDrag}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
          onLostPointerCapture={stopDragging}
          data-testid={suffixTestId('previewDivider', props)}
        >
          <ToggleButton
            type="button"
            $isCollapsed={isCollapsed}
            $enhancedSurfaces={enhancedSurfaces}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={handleToggle}
            aria-label={toggle.label}
            data-testid={suffixTestId('previewToggle', props)}
          >
            <Icon value={toggle.icon} size={4} color="text.primary" />
          </ToggleButton>
        </Divider>
        <Show when={!isCollapsed}>
          <PreviewPanel
            $width={previewWidth}
            $isHeightAuto={options.isHeightAuto}
            data-testid={suffixTestId('previewPanel', props)}
          >
            {props.preview}
          </PreviewPanel>
        </Show>
      </Show>
    </Container>
  );
}

const Container = styled.div<{$isDragging: boolean; $isHeightAuto?: boolean}>`
  display: flex;
  width: 100%;
  ${({$isHeightAuto}) =>
    $isHeightAuto
      ? // The page wraps the layout in a flex column of the viewport height, which would shrink it
        // straight back into that height and defeat the point of growing with the content.
        'min-height: 100%; flex-shrink: 0;'
      : 'height: 100%;'}
  cursor: ${({$isDragging}) => ($isDragging ? 'col-resize' : 'auto')};
  user-select: ${({$isDragging}) => ($isDragging ? 'none' : 'auto')};
`;

const MainPanel = styled.div<{$isHeightAuto?: boolean}>`
  flex: 1 1 auto;
  min-width: 0;
  ${({$isHeightAuto}) => ($isHeightAuto ? '' : 'height: 100%;')}
`;

const PreviewPanel = styled.div<{$width: number; $isHeightAuto?: boolean}>`
  flex: 0 0 ${({$width}) => $width}%;
  min-width: 0;
  ${({$isHeightAuto}) => ($isHeightAuto ? '' : 'height: 100%;')}
`;

const Divider = styled.div<{
  $isCollapsed: boolean;
  $isDragging: boolean;
  $enhancedSurfaces: boolean;
}>`
  position: relative;
  flex-shrink: 0;
  width: ${({theme, $isCollapsed}) => theme.getSize($isCollapsed ? 6 : 2)};
  height: 100%;
  cursor: ${({$isCollapsed}) => ($isCollapsed ? 'default' : 'col-resize')};
  touch-action: none;
  background-color: ${({theme, $isCollapsed, $isDragging}) => {
    if ($isCollapsed) {
      return theme.colors.general.white;
    }

    return $isDragging ? theme.colors.general.accent : 'transparent';
  }};
  border-left: 1px solid
    ${({theme, $isCollapsed}) =>
      $isCollapsed ? theme.colors.palettes.neutral[40][100] : 'transparent'};
  transition: background-color 0.2s ease-in-out;
  ${({theme, $isDragging, $enhancedSurfaces}) =>
    $enhancedSurfaces &&
    css`
      @media (hover: hover) {
        &:hover {
          background-color: ${$isDragging
            ? theme.colors.general.accent
            : theme.colors.palettes.blue[20][100]};
        }
      }
      &:focus-visible {
        outline: ${theme.outline.default} ${theme.colors.general.accent};
        outline-offset: calc(-1 * ${theme.outline.offset});
      }
      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    `}
`;

/**
 * Anchored near the top of the divider rather than vertically centered - that is where the eye
 * starts on a long list. It is centered on the visible separator (the preview's left border when
 * expanded, the divider's own border when collapsed), so it looks the same in both states.
 */
const ToggleButton = styled.button<{$isCollapsed: boolean; $enhancedSurfaces: boolean}>`
  position: absolute;
  top: ${({theme}) => theme.getSize(6)};
  left: ${({$isCollapsed}) => ($isCollapsed ? '0' : '100%')};
  z-index: 1;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({theme}) => theme.getSize(6)};
  height: ${({theme}) => theme.getSize(12)};
  padding: 0;
  border: 1px solid ${({theme}) => theme.colors.palettes.neutral[40][100]};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme}) => theme.colors.general.white};
  box-shadow: ${({theme}) => theme.shadows.elevation_2};
  color: ${({theme}) => theme.colors.text.primary};
  cursor: pointer;
  ${({theme, $enhancedSurfaces}) =>
    $enhancedSurfaces &&
    css`
      user-select: none;
      transition:
        background-color 150ms ease,
        border-color 150ms ease,
        box-shadow 150ms ease;
      @media (hover: hover) {
        &:hover {
          background-color: ${theme.colors.palettes.blue[20][100]};
          border-color: ${theme.colors.general.accent};
          box-shadow: ${theme.shadows.elevation_3};
        }
      }
      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    `}

  &:focus {
    outline: none;
  }

  /* Keyboard users still get a ring, a mouse click leaves nothing behind. */
  &:focus-visible {
    outline: ${({theme, $enhancedSurfaces}) =>
        $enhancedSurfaces ? theme.outline.default : '2px solid'}
      ${({theme}) => theme.colors.general.accent};
    outline-offset: ${({theme, $enhancedSurfaces}) =>
      $enhancedSurfaces ? theme.outline.offset : '2px'};
  }
`;
